/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-02-14 00:00:00
 *
 *******************************************************/
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Page,
  Button,
  Layout,
  Card,
  Badge,
  Icon,
  TextStyle,
  DataTable,
  Pagination,
  SkeletonDisplayText,
  Grid
} from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import TitleHelp from 'src/components/views/common/title-help';
import { URL } from 'src/constants/links';

import { routes } from 'src/routes';
import history from 'src/routes/history';
// import { getShop } from 'src/helpers';
import { trackFSEvent } from 'src/integrations/fullStory';
import { isThemeTurbo, hasOldThemeVersion, getVersionTextSI } from 'src/helpers/theme-setup-helper';

import * as shopifyIntegrationThemeAction from 'actions/shopify-integration/theme-actions';
import * as themeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';
import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';
import { checkUserEnableShowAppVersionSwitching } from 'src/helpers/account-helper';
import { getFullAppUrlThemeEditor } from 'src/helpers';

import {
  ExternalMinor,
  StarFilledMinor,
  ThemesMajor,
  SettingsMajor,
  ColorsMajor
} from '@shopify/polaris-icons';
import IconSync from 'src/assets/images/common/sync.svg';

import ThemeTurboWarning, {
  WarningType
} from 'src/components/views/theme-setup-v3/theme-setup-form/theme-turbo-warning';
import RemoveOldVersionConfirmModal from './modal/remove-old-version-confirm.modal';
import WarningCheckingThemeModal from './modal/warning-checking-theme';
import BannerUpgradeTurbo, {
  KEY_BOOST_TURBO_BANNER_SI
} from 'src/components/containers/dashboard-v3/banner-upgrade-turbo';
import { getShop } from 'src/helpers';
import { isInWhiteListTurbo, isInWhiteListTurboV2 } from 'src/helpers/whitelist';
import ThumbnailDefault from 'assets/images/shopify-integration/ThumbnailDefaultTemplate224.png';
import ThumbnailBasic from 'assets/images/shopify-integration/ThumbnailBasicTemplate224.png';
import { MAPPING_THEME_NAME } from 'src/constants/theme-settings';

type Props = {};
type ComposedProps = Props & WithI18nProps;
type State = {
  activeModalRemoveOldVersionConfirm: boolean
};

const PAGE_SIZE_THEMES = 5;

class ShopifyIntegrationScreenList extends Component<ComposedProps, State> {
  state = {
    activeModalRemoveOldVersionConfirm: false,
    rows: [],
    page: 1,
    totalPage: 0,
    warningThemeInfo: null,
    isShowWarningCheckTheme: false,
    warningType: ''
  };

  async componentDidMount() {
    const { getAllThemes } = this.props;
    getAllThemes();
  }

  componentWillReceiveProps(nextProps) {
    const {
      getAllThemesLoading: getAllThemesLoadingPrev,
      removeOldThemeLoading: removeOldThemeLoadingPrev
    } = this.props;
    // const enableAppVersionSwitching = checkUserEnableShowAppVersionSwitching(tenantApp);

    let { getAllThemesLoading, themes } = nextProps;
    if (!getAllThemesLoading && getAllThemesLoadingPrev) {
      if (!_.isEmpty(themes)) {
        const newTotalPage = Math.ceil(themes.length / PAGE_SIZE_THEMES);
        this.setState({
          totalPage: newTotalPage
        });
        this.paginateData(1, themes);
      }
    }

    if (
      removeOldThemeLoadingPrev &&
      !nextProps.removeOldThemeLoading &&
      nextProps.removeOldThemeSuccess
    ) {
      this.setState({ activeModalRemoveOldVersionConfirm: false });
    }
  }

  handleSelectTheme = async selectedTheme => {
    const { updateFieldReducer, tenantApp, checkThemeLoading, themes } = this.props;
    const enableAppVersionSwitching = checkUserEnableShowAppVersionSwitching(tenantApp);

    updateFieldReducer('selectedThemeId', selectedTheme?.id);

    if (enableAppVersionSwitching) {
      // const checkResult = await checkThemeWithTurbo(selectedTheme?.id);
      const themeFullInfo = _.find(themes, { id: selectedTheme?.id });

      let warningThemeInfo = null;
      let warningType = '';
      // https://apphub-workspace.slack.com/archives/C06CRR8RJSU/p1714009081431069?thread_ts=1713344365.084669&cid=C06CRR8RJSU
      // Live theme not install any version + Live theme install old version (v1,v2,v3)
      if (
        !checkThemeLoading &&
        themeFullInfo?.role === 'main' &&
        (hasOldThemeVersion(themeFullInfo) ||
          (!isThemeTurbo(themeFullInfo) && !hasOldThemeVersion(themeFullInfo)))
      ) {
        warningThemeInfo = (
          <ThemeTurboWarning
            tenantApp={tenantApp}
            type={WarningType.IN_SHOPIFY_INTEGRATION_THEME_LIVE}
            isDefaultColor
          />
        );
        warningType = WarningType.IN_SHOPIFY_INTEGRATION_THEME_LIVE;
      } else if (
        !checkThemeLoading &&
        themeFullInfo &&
        hasOldThemeVersion(themeFullInfo) &&
        !isThemeTurbo(themeFullInfo)
      ) {
        warningThemeInfo = (
          <ThemeTurboWarning
            tenantApp={tenantApp}
            type={WarningType.IN_SHOPIFY_INTEGRATION_THEME_V3}
            onRemoveOldVersion={this.onRemoveOldVersion}
            isShowButton={false}
            isDefaultColor
          />
        );
        warningType = WarningType.IN_SHOPIFY_INTEGRATION_THEME_V3;
      } else if (
        !checkThemeLoading &&
        themeFullInfo &&
        hasOldThemeVersion(themeFullInfo) &&
        isThemeTurbo(themeFullInfo)
      ) {
        warningThemeInfo = (
          <ThemeTurboWarning
            tenantApp={tenantApp}
            type={WarningType.IN_SHOPIFY_INTEGRATION_THEME_V3_MIXED_TURBO}
            onRemoveOldVersion={this.onRemoveOldVersion}
            isShowButton={false}
            isDefaultColor
          />
        );
        warningType = WarningType.IN_SHOPIFY_INTEGRATION_THEME_V3_MIXED_TURBO;
      }

      this.setState({
        warningThemeInfo,
        isShowWarningCheckTheme: !!warningThemeInfo,
        warningType
      });

      if (!!warningThemeInfo) {
        return;
      }
    }

    this.onNext(selectedTheme?.id);
  };

  onRemoveOldVersion = () => {
    this.setState({ activeModalRemoveOldVersionConfirm: true });
  };

  handleRemove = async () => {
    const { selectedThemeId, themes, updateAllThemes } = this.props;
    const themeFullInfo = _.find(themes, { id: selectedThemeId });

    const { removeOldVersionTheme } = this.props;
    await removeOldVersionTheme({
      themeId: selectedThemeId,
      theme: themeFullInfo
    });

    const allThemes = _.cloneDeep(themes);
    const themeIndex = _.findIndex(allThemes, { id: selectedThemeId });
    if (themeIndex >= 0) {
      allThemes[themeIndex].taeFeatures = null;
      allThemes[themeIndex].feLibVersion = null;
      allThemes[themeIndex].enabledBoostCoreV3 = false;

      const theme = allThemes.splice(themeIndex, 1);
      if (!_.isEmpty(theme)) {
        allThemes.splice(1, 0, theme[0]);
        this.paginateData(1, allThemes);
        this.setState({
          page: 1
        });
        updateAllThemes(allThemes);
      }
    }
  };

  handleCancel = () => {
    this.setState({ activeModalRemoveOldVersionConfirm: false });
  };

  checkAllowedShowBannerTurbo = () => {
    const { tenantApp } = this.props;
    const enableAppVersionSwitching = checkUserEnableShowAppVersionSwitching(tenantApp);

    try {
      const isInWhiteList =
        isInWhiteListTurbo(tenantApp.tenantId) || isInWhiteListTurboV2(tenantApp.tenantId);
      const isAllowedShowBannerTurbo =
        !localStorage.getItem(`${getShop()}-${KEY_BOOST_TURBO_BANNER_SI}`) &&
        isInWhiteList &&
        enableAppVersionSwitching;

      return isAllowedShowBannerTurbo;
    } catch (e) {
      console.log('error', e);
    }
  };

  handleOpenTurboLandingPage = () => {
    history.push(routes.landing.turbo());
  };

  nextPage = () => {
    const { page, totalPage } = this.state;
    const { themes } = this.props;
    if (page === totalPage) {
      return;
    }

    this.paginateData(page + 1, themes);
    this.setState({
      page: page + 1
    });
  };

  previousPage = () => {
    const { page } = this.state;
    const { themes } = this.props;
    if (page === 1) {
      return;
    }

    this.paginateData(page - 1, themes);
    this.setState({
      page: page - 1
    });
  };

  paginateData = (page, data) => {
    this.setState({
      rows: _.cloneDeep(data).slice((page - 1) * PAGE_SIZE_THEMES, page * PAGE_SIZE_THEMES)
    });
  };

  handleCancelWarningRemove = () => {
    this.setState({
      isShowWarningCheckTheme: false
    });
  };

  handleWarningRemove = () => {
    this.setState({
      isShowWarningCheckTheme: false,
      activeModalRemoveOldVersionConfirm: true
    });
  };

  goToOnlineStore = () => {
    const previewUrl = getFullAppUrlThemeEditor();
    window.open(previewUrl);
  };

  handleToManageTemplate = () => {
    const { updateFieldReducer } = this.props;
    updateFieldReducer('selectedThemeId', '');
    history.push(routes.shopifyIntegration.templateLibrary());
  };

  render() {
    const {
      activeModalRemoveOldVersionConfirm,
      rows,
      page,
      totalPage,
      isShowWarningCheckTheme,
      warningThemeInfo,
      warningType
    } = this.state;
    const {
      i18n,
      themes,
      getAllThemesLoading,
      checkingTheme,
      selectedThemeId,
      // tenantApp,
      checkThemeLoading,
      removeOldThemeLoading
    } = this.props;
    // const enableAppVersionSwitching = checkUserEnableShowAppVersionSwitching(tenantApp);

    const isAllowedShowBannerTurbo = this.checkAllowedShowBannerTurbo();
    const displayRows = getAllThemesLoading
      ? [1, 2, 3, 4, 5].map(item => [
          <div className="data-cell-inner data-cell-inner--default">
            <SkeletonDisplayText />
          </div>,
          <div className="data-cell-inner data-cell-inner--default">
            <SkeletonDisplayText />
          </div>,
          <div className="data-cell-inner data-cell-inner--default">
            <SkeletonDisplayText />
          </div>
        ])
      : rows.map(item => [
          <div
            className="data-cell-inner"
            style={{ display: 'flex', alignItems: 'center', gap: 2 }}
            onClick={() => this.handleSelectTheme(item)}>
            <span>{item.name}</span>
            {item.role === 'main' && (
              <span id="ssr-selected-theme-item" className="live">
                <Badge>
                  {i18n.translate('AdminHTML.ShopifyIntegration.ThemeStatus.liveTheme')}
                </Badge>
              </span>
            )}
          </div>,
          <div className="data-cell-inner" onClick={() => this.handleSelectTheme(item)}>
            {getVersionTextSI(item, i18n)}
          </div>,
          <div className="data-cell-inner" onClick={() => this.handleSelectTheme(item)}>
            <Button
              icon={<Icon source={SettingsMajor} />}
              onClick={e => {
                e.stopPropagation();
                this.handleSelectTheme(item);
              }}
              loading={item.id === selectedThemeId && (checkingTheme || checkThemeLoading)}
              disabled={item.id !== selectedThemeId && (checkingTheme || checkThemeLoading)}
            />
          </div>
        ]);

    return (
      <Page title={i18n.translate('AdminHTML.ShopifyIntegration.Integration.pageTitle')}>
        <TitleHelp
          i18n={i18n}
          hrefUserManual={URL['Link_V2_Theme_ThemeSetup_ManualGuide']}
          // hrefFAQ={URL['ThemeSetupV3Doc_FAQ']}
        >
          <div className="BC-Page__Title--Help__Item">
            <span onClick={this.handleOpenTurboLandingPage}>
              <Icon source={StarFilledMinor} color="base" />
            </span>
            <span onClick={this.handleOpenTurboLandingPage}>
              {i18n.translate('AdminHTML.Common.Action.seeWhatNewInTurbo')}
            </span>
          </div>
        </TitleHelp>

        {isAllowedShowBannerTurbo && <BannerUpgradeTurbo />}

        <div className="main-layout">
          <Layout>
            <Layout.Section>
              <Card
                title={
                  <div className="cardTitle">
                    <div>
                      <p className="Polaris-Heading">
                        {i18n.translate('AdminHTML.ShopifyIntegration.ScreenList.listOfTheme')}
                      </p>
                      <TextStyle variation="subdued">
                        {i18n.translate('AdminHTML.ShopifyIntegration.ScreenList.listOfTheme_desc')}
                      </TextStyle>
                    </div>
                    <Button
                      icon={
                        <img
                          className={`select-theme-refresh ${
                            getAllThemesLoading ? 'refreshing' : ''
                          }`}
                          src={IconSync}
                          alt="refresh"
                          width={20}
                          height={20}
                        />
                      }
                      disabled={checkThemeLoading || checkingTheme}
                      onClick={this.onRefreshThemeList}>
                      {i18n.translate('AdminHTML.ShopifyIntegration.ScreenList.refresh')}
                    </Button>
                  </div>
                }>
                <Card.Section>
                  {_.isEmpty(themes) && !getAllThemesLoading && (
                    <div className="empty-block">
                      <TextStyle variation="subdued">
                        {i18n.translate('AdminHTML.ShopifyIntegration.ScreenList.emptyTheme')}
                      </TextStyle>
                      <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <Button primary onClick={this.goToOnlineStore}>
                          {i18n.translate('AdminHTML.ShopifyIntegration.Actions.gotoOnlineStore')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {(!_.isEmpty(themes) || getAllThemesLoading) && (
                    <DataTable
                      columnContentTypes={['text', 'text', 'text']}
                      headings={[
                        i18n.translate('AdminHTML.ShopifyIntegration.ScreenList.table_Title1'),
                        i18n.translate('AdminHTML.ShopifyIntegration.ScreenList.table_Title2'),
                        i18n.translate('AdminHTML.ShopifyIntegration.ScreenList.table_Title3')
                      ]}
                      rows={displayRows}
                      verticalAlign="middle"
                    />
                  )}
                  {!_.isEmpty(themes) && !getAllThemesLoading && totalPage > 1 && (
                    <div className="BC-Page__ShopifyIntegration__Pagination">
                      <Pagination
                        label={`${page}/${totalPage}`}
                        hasPrevious={page > 1}
                        onPrevious={this.previousPage}
                        hasNext={page < totalPage}
                        onNext={this.nextPage}
                      />
                    </div>
                  )}
                </Card.Section>
              </Card>

              {/* Template library */}
              <Card
                title={
                  <div className="cardTitle">
                    <div>
                      <p className="Polaris-Heading">
                        {i18n.translate('AdminHTML.ShopifyIntegration.ScreenList.templateLibrary')}
                      </p>
                      <TextStyle variation="subdued">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.ScreenList.templateLibraryDesc'
                        )}
                      </TextStyle>
                    </div>
                    <Button onClick={this.handleToManageTemplate}>
                      {i18n.translate('AdminHTML.ShopifyIntegration.Actions.manageTemplate')}
                    </Button>
                  </div>
                }>
                <Card.Section>
                  <div className="manage-template-block">
                    <Grid gap={{ xs: 16 }}>
                      <Grid.Cell columnSpan={{ xs: 3, lg: 6 }}>
                        <div className="template-item">
                          <img src={ThumbnailDefault} alt="thumbnail" width={224} />
                          <div className="template-item__info">
                            <p style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                              {MAPPING_THEME_NAME['default']}
                            </p>
                            <TextStyle variation="subdued">
                              {i18n.translate('AdminHTML.ShopifyIntegration.ScreenList.createdBy')}
                            </TextStyle>
                          </div>
                        </div>
                      </Grid.Cell>
                      <Grid.Cell columnSpan={{ xs: 3, lg: 6 }}>
                        <div className="template-item">
                          <img src={ThumbnailBasic} alt="thumbnail" width={224} />
                          <div className="template-item__info">
                            <p style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                              {MAPPING_THEME_NAME['dawn']}
                            </p>
                            <TextStyle variation="subdued">
                              {i18n.translate('AdminHTML.ShopifyIntegration.ScreenList.createdBy')}
                            </TextStyle>
                          </div>
                        </div>
                      </Grid.Cell>
                    </Grid>
                  </div>
                </Card.Section>
              </Card>

              {/* Helper */}
              <Card
                title={
                  <div className="cardTitle">
                    <div>
                      <p className="Polaris-Heading">
                        {i18n.translate('AdminHTML.ShopifyIntegration.ScreenList.needHelp')}
                      </p>
                      <TextStyle variation="subdued">
                        {i18n.translate('AdminHTML.ShopifyIntegration.ScreenList.needHelp_desc')}
                      </TextStyle>
                    </div>
                  </div>
                }>
                <Card.Section>
                  <div className="helper-block">
                    <Button
                      external
                      url={URL['BoostTemplateWidgets']}
                      plain
                      icon={<Icon source={ThemesMajor} />}>
                      {i18n.translate(
                        'AdminHTML.ShopifyIntegration.SetupGuide.boostTemplateWidget'
                      )}
                      <Icon source={ExternalMinor} />
                    </Button>
                    <Button
                      external
                      url={URL['SetupWidgetForTheme']}
                      plain
                      icon={<Icon source={SettingsMajor} />}>
                      {i18n.translate(
                        'AdminHTML.ShopifyIntegration.SetupGuide.setupWidgetForTheme'
                      )}
                      <Icon source={ExternalMinor} />
                    </Button>
                    <Button
                      external
                      url={URL['EnhancedVisualEditor']}
                      plain
                      icon={<Icon source={ColorsMajor} />}>
                      {i18n.translate(
                        'AdminHTML.ShopifyIntegration.SetupGuide.customizeYourTemplate'
                      )}
                      <Icon source={ExternalMinor} />
                    </Button>
                  </div>
                </Card.Section>
              </Card>
            </Layout.Section>
          </Layout>
        </div>
        <RemoveOldVersionConfirmModal
          active={activeModalRemoveOldVersionConfirm}
          handleRemove={this.handleRemove}
          handleCancel={this.handleCancel}
          removeOldThemeLoading={removeOldThemeLoading}
        />

        <WarningCheckingThemeModal
          active={isShowWarningCheckTheme}
          onCancel={this.handleCancelWarningRemove}
          onRemove={this.handleWarningRemove}
          warningThemeInfo={warningThemeInfo}
          warningType={warningType}
        />
      </Page>
    );
  }

  onNext = async (selectedThemeId: string) => {
    const {
      themes,
      // handleChangeScreen,
      saveSelectedTheme
    } = this.props;

    if (!selectedThemeId) {
      return;
    }

    // Call API to check integration status for specific theme
    // const result = await checkThemeIntegrationStatus(selectedThemeId);
    const result = _.find(themes, { id: selectedThemeId });

    // Get template instance from response data
    const integratedTemplateInstance = _.get(result, 'templateInstance');

    // Save selected theme to state => Support page back and display theme details in other screen
    saveSelectedTheme(selectedThemeId);

    // Select a theme that hasn't linked any templates yet
    if (_.isEmpty(integratedTemplateInstance)) {
      history.push(routes.shopifyIntegration.newThemeWidget(selectedThemeId));
      return;
    }

    // Select a theme that has linked a template
    // saveIntegratedTemplateInstance(integratedTemplateInstance);

    history.push(routes.shopifyIntegration.viewThemeWidget(selectedThemeId));

    // Track Full Story event
    const themeDetails = _.find(themes, { id: selectedThemeId });
    trackFSEvent('Select Theme Setup', {
      feature: 'SSR Theme Setup',
      selectedTheme: themeDetails?.name
    });
  };

  onRefreshThemeList = () => {
    const { getAllThemes } = this.props;
    this.setState({
      page: 1
    });
    getAllThemes();
  };
}

const mapStateToProps = (state, props) => {
  const { shopifyIntegrationTheme = {}, themeIntegration = {}, themeSetupV3 = {} } = state;

  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const themes = _.get(shopifyIntegrationTheme, 'allThemes', []);
  const selectedTheme = _.get(themeIntegration, 'selectedTheme', {});

  return {
    tenantApp,
    themes,
    getAllThemesLoading: _.get(shopifyIntegrationTheme, 'getAllThemesLoading', true),
    selectedTheme,
    selectedThemeId: _.get(themeIntegration, 'selectedThemeId', ''),
    // Check theme integration status
    checkingTheme: _.get(themeIntegration, 'checkingTheme', false),
    integratedTemplateInstance: _.get(themeIntegration, 'templateInstance', {}),
    checkThemeLoading: _.get(themeSetupV3, 'checkThemeLoading', false),
    themeCheck: _.get(themeSetupV3, 'themeCheck', null),
    themeCheckId: _.get(themeSetupV3, 'themeCheckId', null),

    removeOldThemeLoading: _.get(themeSetupV3, 'removeOldThemeloading', false),
    removeOldThemeSuccess: _.get(themeSetupV3, 'removeOldThemeSuccess', false),
    removeOldThemeError: _.get(themeSetupV3, 'removeOldThemeError', undefined),
    removeOldTheme: _.get(themeSetupV3, 'removeOldTheme', {})
  };
};
const mapDispatchToProps = (dispatch, ownProps) => ({
  getAllThemes: () => dispatch(shopifyIntegrationThemeAction.getAllThemes(ownProps.i18n)),
  updateAllThemes: data => dispatch(shopifyIntegrationThemeAction.updateAllThemes(data)),
  // checkThemeIntegrationStatus: themeId =>
  //   dispatch(themeIntegrationAction.checkThemeIntegrationStatus(ownProps.i18n, themeId)),
  updateFieldReducer: (fieldName, data) =>
    dispatch(themeIntegrationAction.handleUpdateField(fieldName, data)),
  checkThemeWithTurbo: themeId =>
    dispatch(themeSetupV3Action.checkThemeWithTurbo(themeId, ownProps.i18n)),
  removeOldVersionTheme: data =>
    dispatch(themeSetupV3Action.removeOldVersionTheme(data, ownProps.i18n, true))
});

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(ShopifyIntegrationScreenList)
);
